import { Absence } from "../../models/absences";
import { Duty } from "../../models/duties";
import { Events } from "../../models/events";
import { User } from "../../modules/security/models/user-model";
import { EmploymentTaskState } from "../../models/task";
import { useDialog } from "../modal";
import { UserActivation } from "@/models/userActivations";
import store from "../../store";
import { Employee } from "@/models/employees";
import EventsHandler from "@/handlers/events-handler";
import { Employment } from "@/models/employments";
import { AbsenceRequest } from "@/models/absenceRequest-model";

export async function AbsenceDialog(
  employeeId: number | undefined,
  Absence: Absence | undefined,
  absences: Absence[] = [],
  absenceRequest: AbsenceRequest | undefined,
  multipleEmployees = false
): Promise<Absence[] | AbsenceRequest | undefined> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "absenceDialog",
        component: import("./absenceDialog.vue"),
        options: {
          destroy: true
        },
        props: {
          EmployeeId: employeeId,
          Absence: Absence,
          MultipleEmployees: multipleEmployees,
          Absences: absences,
          AbsenceRequest: absenceRequest
        }
      })
      .then((absences: Absence[] | AbsenceRequest | undefined) => {
        resolve(absences);
      })
      .catch((error) => reject(error));
  });
}

export async function AbsenceListDialog(employeeId: number, Absences: Absence[]): Promise<boolean> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "absenceListDialog",
        component: import("./absenceListDialog.vue"),
        options: {
          destroy: true
        },
        props: { EmployeeId: employeeId, absences: Absences }
      })
      .then((ok: boolean) => resolve(ok))
      .catch((error) => reject(error));
  });
}
export async function ShowTaskDialog(
  employeeTask: EmploymentTaskState | null
): Promise<EmploymentTaskState | null> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "taskDialog",
        component: import("./taskDialog.vue"),
        options: {
          destroy: true
        },
        props: { employeeTask: employeeTask }
      })
      .then((ok: EmploymentTaskState | null) => resolve(ok))
      .catch((error) => reject(error));
  });
}

export async function ShowUserProfileDialog(user: User): Promise<boolean> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "userProfileDialog",
        component: import("./userProfileDialog.vue"),
        options: {
          destroy: true
        },
        props: { user: user }
      })
      .then((ok: boolean) => resolve(ok))
      .catch((error) => reject(error));
  });
}

export async function ShowChangePasswordDialog(user: User): Promise<boolean> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "changePasswordDialog",
        component: import("./changePasswordDialog.vue"),
        options: {
          destroy: true
        },
        props: { user: user }
      })
      .then((ok: boolean) => resolve(ok))
      .catch((error) => reject(error));
  });
}

export async function CreateEmployeeDialog(): Promise<Employee> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "createEmployeeDialog",
        component: import("./createEmployeeDialog.vue"),
        options: {
          destroy: true
        }
      })
      .then((employee: Employee) => {
        resolve(employee);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export async function EmploymentDialog(
  employeeId: number,
  employmentId: number | undefined = undefined,
  positionId: number | undefined = undefined
): Promise<Employment> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "employmentDialog",
        component: import("./employmentDialog.vue"),
        options: {
          destroy: true
        },
        props: { employeeId: employeeId, employmentId: employmentId, positionId: positionId }
      })
      .then((employment: Employment) => resolve(employment))
      .catch((error) => reject(error));
  });
}

export async function SplitEmploymentDialog(
  employeeId: number,
  employmentId: number | undefined = undefined,
  positionId: number | undefined = undefined
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "splitEmploymentDialog",
        component: import("./splitEmploymentDialog.vue"),
        options: {
          destroy: true
        },
        props: { employeeId: employeeId, employmentId: employmentId, positionId: positionId }
      })
      .then((ok: boolean) => resolve(ok))
      .catch((error) => reject(error));
  });
}

export async function EmploymentCreateDialog(
  employeeId: number,
  positionId: number,
  employmentId?: number
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "EmploymentCreateDialog",
        component: import("./../../components/dialogs/employmentDialog.vue"),
        options: {
          destroy: true
        },
        props: {
          employeeId: employeeId,
          positionId: positionId,
          employmentId: employmentId
        }
      })
      .then((ok: boolean) => resolve(ok))
      .catch((error) => reject(error));
  });
}

export async function ListDialog(
  duties: Duty[] | undefined,
  events: Events[] | undefined,
  employee: Employee | null = null
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "listDialog",
        component: import("./listDialog.vue"),
        options: {
          destroy: true
        },
        props: { Duties: duties, Events: events, Employee: employee }
      })
      .then((ok: boolean) => resolve(ok))
      .catch((error) => reject(error));
  });
}

export async function ListDialogWithEmpeInfo(
  duties: Duty[] | undefined,
  events: Events[] | undefined,
  employee: Employee
): Promise<boolean> {
  return ListDialog(duties, events, employee);
}

export async function DutyDialog(
  duty: Duty | undefined = undefined,
  fromList = false
): Promise<Duty | null> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "dutyDialog",
        component: import("./dutyDialog.vue"),
        options: {
          destroy: true
        },
        props: { Duty: duty, FromList: fromList }
      })
      .then((duty: Duty | null) => resolve(duty))
      .catch((error) => reject(error));
  });
}

export async function ChangeToUniLoginDialog(
  userActivation: UserActivation[] | null
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "changeLoginToUniloginDialog",
        component: import("./changeLoginToUnilogin.vue"),
        options: {
          destroy: true
        },
        props: { userActivations: userActivation }
      })
      .then((ok: boolean) => resolve(ok))
      .catch((error) => reject(error));
  });
}

export async function EventDialog(
  event: Events | undefined = undefined,
  fromList = false,
  employeeIds: number[] = [],
  editAllRecurrentEvents: boolean | undefined = false
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "eventDialog",
        component: import("./eventDialog.vue"),
        options: {
          destroy: true
        },
        props: {
          EventValue: event,
          Employeeids: employeeIds,
          FromList: fromList
        }
      })
      .then((ok: boolean) => resolve(ok))
      .catch((error) => reject(error));
  });
}

export async function OverlappingEventsDialog(
  overlappingEvents: {
    person: string;
    start: Date;
    end: Date;
    event: string;
  }[] = [],
  eventDates: Date
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    useDialog()
      .open({
        id: "overlappingEventsDialog",
        component: import("./overlappingEventsDialog.vue"),
        options: {
          destroy: true
        },
        props: {
          OverlappingEvents: overlappingEvents,
          EventDate: eventDates
        }
      })
      .then((ok: boolean) => resolve(ok))
      .catch((error) => reject(error));
  });
}

export async function ShowEventDialog(event: Events, fromList = false): Promise<boolean> {
  return new Promise((resolve, reject) => {
    return EventDialog(event, fromList)
      .then((ok: boolean) => {
        resolve(ok);
      })
      .catch((error) => reject(error));
  });
}

export async function ConditionalShowEventDialog(
  event: Events,
  fromList = false,
  employee: Employee | null
): Promise<boolean> {
  const user: User = store.getters["security/getUser"]();
  let showDialog = false;

  if (user.isManager()) {
    showDialog = true;
  }

  if (user.isEmployee() && user.employee_id == employee?.id) {
    showDialog = true;
  }

  if (showDialog) {
    return ShowEventDialog(event, fromList);
  } else {
    EventsHandler.showEventWarning();
    return false;
  }
}
